/**
 * The two rules every order maximum shares, whatever it is trading.
 *
 * `resolveOrderPricer` turns money into size and back (a limit order at its
 * own price; anything else walked through the book, priced at the mark beyond
 * its depth). `applyOrderSizeLimits` is the LAST step of every maximum — the
 * step floor, the `max_order_size` cap and the market minimum, decided in one
 * place so spot and perps cannot drift apart.
 *
 * They lived in `perpOrderMax` and were imported from there by `spotOrderMax`,
 * which is exactly the sort of borrowing this module exists to stop.
 */
import { bestLevel, BookLevels, BookSide, quoteForSize, sizeForQuote } from "../book";
import { Dec, dec } from "../decimal";
import { MarketSteps } from "../spec";
import { MaxLimiter, MaxResult, maxOk, maxUnavailable } from "./types";

/** Base sizes divide exactly onto 1e-18, the finest grid anything on-chain uses. */
const SIZE_DECIMALS = 18;

const positive = (value: Dec | number | null | undefined): Dec | null => {
  if (value === null || value === undefined) return null;
  const parsed = dec.parse(value);
  return parsed !== null && dec.gt(parsed, "0") ? parsed : null;
};

/** A book with at least one real quote on it — padding rows are not depth. */
const hasDepth = (levels: BookLevels, side: BookSide): boolean =>
  bestLevel(levels, side) !== null;

/** Converts between the two terms an order can be entered in. */
export interface Pricer {
  toBase(quote: Dec): Dec;
  toQuote(base: Dec): Dec;
}

/** Everything that decides how an order converts size to money, and back. */
export interface OrderPricingInput {
  side: "buy" | "sell";
  orderType: "market" | "limit";
  limitPrice?: Dec;
  book?: { bids?: BookLevels; asks?: BookLevels };
  mark?: Dec;
}

/**
 * How this order converts between size and USD.
 *
 * A limit order uses its own price. Anything else walks the book, pricing what
 * the book cannot cover at the mark; with no book, the mark alone. A stale
 * limit price is NEVER used for a market order — doing that once sized a market
 * order larger than the amount the trader typed.
 *
 * Exported because a spot maximum converts terms by exactly the same rules.
 */
export const resolveOrderPricer = (input: OrderPricingInput): Pricer | null => {
  const limitPrice = positive(input.limitPrice);
  if (input.orderType === "limit" && limitPrice !== null) {
    return {
      toBase: (quote) =>
        dec.div(quote, limitPrice, SIZE_DECIMALS, "floor") ?? "0",
      toQuote: (base) => dec.mul(base, limitPrice),
    };
  }

  // A buy is filled by the asks, a sell by the bids.
  const bookSide: BookSide = input.side === "buy" ? "asks" : "bids";
  const levels = input.book?.[bookSide];
  const mark = positive(input.mark);
  if (hasDepth(levels, bookSide)) {
    return {
      toBase: (quote) => sizeForQuote(levels, quote, mark).size,
      toQuote: (base) => quoteForSize(levels, base, mark).quote,
    };
  }

  if (mark !== null) {
    return {
      toBase: (quote) => dec.div(quote, mark, SIZE_DECIMALS, "floor") ?? "0",
      toQuote: (base) => dec.mul(base, mark),
    };
  }
  return null;
};

/**
 * The market's own ceiling and floor, applied to a base size.
 *
 * The last step of EVERY order maximum — spot included, which is why this is
 * exported — so the step floor, the `max_order_size` cap and the minimum are
 * decided in exactly one place. Nothing is ever rounded UP to reach the
 * minimum: that would spend money the trader has not got.
 */
export const applyOrderSizeLimits = (
  baseSize: Dec,
  steps: MarketSteps,
  limitedBy: MaxLimiter,
  unit: string
): MaxResult => {
  let size = dec.toStep(baseSize, steps.amountStep, "floor") ?? "0";
  let limiter = limitedBy;

  const cap = positive(steps.maxOrderAmount);
  if (cap !== null) {
    const cappedAt = dec.toStep(cap, steps.amountStep, "floor") ?? cap;
    if (dec.gt(size, cappedAt)) {
      size = cappedAt;
      limiter = "max_order_size";
    }
  }

  if (!dec.gt(size, "0") || dec.lt(size, steps.minOrderAmount)) {
    return maxUnavailable("below_min_order", steps.minOrderAmount);
  }
  return maxOk(size, limiter, unit);
};
